import * as React from "react";
import type { EmailTarget } from "@/lib/conversations";
import { useContactEmailCompose, useEmailCompose, useEmailContact, useSendEmail } from "@/lib/queries";
import { sendFailure } from "@/lib/texts";

/**
 * The composer's email half (ADR-180 in platform): what core-api says of an email before anything
 * is typed, and the send.
 *
 * Where an email goes is the thread's `EmailTarget`: a reply in core-api's email thread, or an email
 * to the contact from their record, which joins their email thread or starts it. Whether it may
 * leave is core-api's to say (`EmailCompose`): email switched off, a thread whose contact's address
 * changed, no address, an address of ours or off the allowed list, do-not-call, a STOP, the day's
 * limit. It also gives the subject a reply takes ("Re: ..."), which the composer fills in.
 */

/** core-api's answer to whether an email may leave now: the email thread's, or the contact's. */
export function useEmailComposeFor(target: EmailTarget | null) {
  const conversation = useEmailCompose(target?.kind === "conversation" ? target.id : undefined);
  const lead = useContactEmailCompose(target?.kind === "lead" ? target.leadId : undefined);
  return target?.kind === "lead" ? lead : conversation;
}

/**
 * Sends an email to where the thread points, and says how it went: sent; kept but not delivered
 * (SendGrid refused it: it is in the thread, marked); refused before it was kept (a 409 with
 * core-api's sentence, or another 4xx); or unconfirmed, when no answer came back to say
 * (`sendFailure`). Held from the press, as a text's send is: core-api cannot tell a second request
 * from a second email, so a double press in between must not send twice.
 */
export function useSendEmailTo() {
  const reply = useSendEmail();
  const first = useEmailContact();
  const inFlight = React.useRef(false);
  const [holding, setHolding] = React.useState(false);
  const send = (
    target: EmailTarget,
    email: { text: string; subject: string },
    on: {
      onSent: () => void;
      onNotDelivered: (why: string | null) => void;
      onRefused: (why: string) => void;
      onUnconfirmed: () => void;
    },
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setHolding(true);
    const done = () => {
      inFlight.current = false;
      setHolding(false);
    };
    const settled = (m: { status: string; error_detail: string | null; error: string | null }) => {
      done();
      if (m.status === "failed") on.onNotDelivered(m.error_detail ?? m.error);
      else on.onSent();
    };
    const failed = (e: unknown) => {
      done();
      const failure = sendFailure(e);
      if (failure.kind === "refused") on.onRefused(failure.detail);
      else on.onUnconfirmed();
    };
    const subject = email.subject || undefined;
    if (target.kind === "conversation") {
      reply.mutate({ conversationId: target.id, text: email.text, subject }, { onSuccess: settled, onError: failed });
    } else {
      first.mutate({ leadId: target.leadId, text: email.text, subject }, { onSuccess: settled, onError: failed });
    }
  };
  return { send, isPending: holding || reply.isPending || first.isPending };
}
